/**
 * Supabase JS client — lazy singleton.
 *
 * Config from env vars set at build time (SWA app settings in prod;
 * .env.local for local dev — never committed).
 *
 * VITE_SUPABASE_URL            — Supabase project URL
 * VITE_SUPABASE_PUBLISHABLE_KEY — anon/publishable key (safe for browser)
 *
 * Lazy initialisation: createClient is called the FIRST time the singleton
 * is dereferenced (any property access on the Proxy). This lets route
 * modules import the singleton at module-load time (e.g. for use inside
 * useEffect handlers) without forcing the env-var check to run during
 * vitest collection — important because vitest doesn't have SWA app
 * settings injected.
 *
 * In production the first call happens before any user interaction, so
 * a missing env var still surfaces immediately at boot.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

let _client: SupabaseClient | null = null;

function getClient(): SupabaseClient {
  if (_client) return _client;
  const url = import.meta.env['VITE_SUPABASE_URL'];
  const anon = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'];
  if (!url || !anon) {
    throw new Error(
      'VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set. ' +
      'Copy frontend/.env.example to frontend/.env.local and fill in the values.',
    );
  }
  _client = createClient(url, anon, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  return _client;
}

// Proxy preserves the `import { supabase } from '...'` ergonomic — any
// property access (supabase.auth.mfa.listFactors(), etc.) goes through
// getClient() and instantiates on first use.
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop, receiver) {
    return Reflect.get(getClient(), prop, receiver);
  },
});

/**
 * Build a throwaway Supabase client that shares the project URL + anon key but
 * is fully isolated from the persisted session: it does not read or write the
 * main client's storage, never auto-refreshes, and never parses the URL.
 *
 * Use this for in-page re-authentication challenges (e.g. confirming the
 * current password before a sensitive change). Calling signInWithPassword on
 * the MAIN client replaces the live session — downgrading a stepped-up (aal2)
 * session to aal1 and clobbering the token in every concurrent tab that shares
 * the storage key. An isolated client confirms the credential without touching
 * the active session.
 *
 * The returned client holds no long-lived state worth disposing; let it fall
 * out of scope after the single challenge.
 */
export function createIsolatedAuthClient(): SupabaseClient {
  const url = import.meta.env['VITE_SUPABASE_URL'];
  const anon = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'];
  if (!url || !anon) {
    throw new Error(
      'VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set. ' +
      'Copy frontend/.env.example to frontend/.env.local and fill in the values.',
    );
  }
  return createClient(url, anon, {
    auth: {
      // Never persist, refresh, or hydrate — this client must not see or mutate
      // the main session in any way.
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      // Distinct storageKey as a belt-and-braces guard in case a future
      // supabase-js version writes anything despite persistSession:false.
      storageKey: 'sb-elreign-reauth-ephemeral',
    },
  });
}
